import "./article-topper.scss";

import cn from "clsx";
import { ReactNode } from "react";
import {
  MediaAsset,
  MediaAssetProps,
} from "design-system/components/primitives/media-asset/media-asset";
import {
  Byline,
  BylineProps,
} from "design-system/components/primitives/byline/byline";
import { Overline, OverlineProps } from "../../primitives/overline/overline";
import { SimpleTheme } from "design-system/utils/theme";
import { EditAttributes } from "design-system/types/types";
import { shouldDecreaseTextSize } from "design-system/utils/text";
import {
  CtaLink,
  CtaLinkProps,
} from "design-system/components/primitives/cta-link/cta-link";

export interface BaseArticleTopperProps {
  title?: string;
  subtitle?: ReactNode;
  overline?: OverlineProps;
  byline?: BylineProps;
  mediaAsset?: MediaAssetProps;
  theme?: SimpleTheme;
  cta?: CtaLinkProps;
  mediaHighlightCaption?: ReactNode;

  editAttributes?: {
    title?: EditAttributes;
    subtitle?: EditAttributes;
    publicationDate?: EditAttributes;
  };
}

export interface ArticleTopperProps extends BaseArticleTopperProps {
  isBigArt?: boolean;
  isKnowledgeBase?: boolean;
}

/**
 * ## See it in use on...
 * - The [kitchen sink detail page](/story/example-pages-detail-pages-kitchen-sink--story)
 *
 * - **`id: CT-002-003-00`**
 * - **`data-region: ct__article__simple`**
 */
export function ArticleTopper({
  title,
  subtitle,
  byline,
  isBigArt,
  isKnowledgeBase,
  mediaAsset,
  overline,
  theme,
  cta,
  mediaHighlightCaption,
  editAttributes,
}: ArticleTopperProps) {
  const noCaption =
    mediaAsset?.hide?.caption ||
    (mediaAsset?.image
      ? mediaAsset?.image?.caption === undefined
      : mediaAsset?.video
        ? mediaAsset?.video?.caption === undefined
        : false);

  if (mediaAsset?.image) {
    mediaAsset.image.loading = "eager";
  }

  const topperClass = cn(
    "hbs-article-topper",
    isBigArt && "hbs-article-topper--big-art",
    noCaption && "hbs-article-topper--no-caption",
    isKnowledgeBase && "hbs-article-topper--knowledge-base",
  );

  const headingClass = cn(
    "hbs-article-topper__heading",
    shouldDecreaseTextSize(title, 30, 5) && "hbs-article-topper__heading--long",
  );
  2;
  const dataRegion = `ct__article__${isBigArt ? "media" : "simple"}`;

  return (
    <div data-theme={theme} data-region={dataRegion}>
      <section className={topperClass}>
        {/* Content */}
        <div className="hbs-article-topper__content">
          {overline && (
            <Overline className="hbs-article-topper__overline" {...overline} />
          )}
          {title && (
            <h1 className={headingClass} {...editAttributes?.title}>
              {title}
            </h1>
          )}
          {subtitle && (
            <div
              className="hbs-article-topper__subheading"
              {...editAttributes?.subtitle}
            >
              {subtitle}
            </div>
          )}

          <div className="hbs-article-topper__meta">
            {!isKnowledgeBase && byline && (
              <span className="hbs-article-topper__meta-item">
                <Byline {...byline} />
              </span>
            )}
          </div>

          {isKnowledgeBase && cta ? (
            <div className="hbs-article-topper__cta">
              <CtaLink {...cta} type="primary-button" hideIcon />
            </div>
          ) : null}
        </div>

        {/* Art */}
        {!isKnowledgeBase && isBigArt && (
          <div className="hbs-article-topper__art">
            {mediaAsset && <MediaAsset {...mediaAsset} aspectRatio="16/9" />}
            {mediaHighlightCaption && (
              <div className="hbs-article-topper__art-caption">
                {mediaHighlightCaption}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
